import React, { useEffect, useState } from "react";
import { useRemoteRenderContext } from "../../../../classes/contexts/RemoteRenderContext";
import { PLANNING_STATES } from "../../../../classes/enums/PlanningStateEnum";
import { vertebraUtils } from "../../../../classes/utils/AnnotationsUtils";
import Button from "../../../../components/button/Button";
import LabeledSelect from "../../../../components/select/LabeledSelect";
import "../VertebraManagement.css";

const AddVertebra = ({ onClose }) => {
  const { client: renderClient } = useRemoteRenderContext();

  const [availableVertebrae, setAvailableVertebrae] = useState({
    frontal: [],
    lateral: [],
  });
  const [selectedVertebra, setSelectedVertebra] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAvailableVertebrae = async () => {
      try {
        setIsLoading(true);
        const data = await renderClient.getAvailableVertebrae(
          PLANNING_STATES.VALIDATE
        );
        setAvailableVertebrae(data);

        // Get vertebrae from whichever view(s) have data
        let vertebraNames = [];

        if (data.frontal.length > 0 && data.lateral.length > 0) {
          // Both views have data - use common vertebrae
          vertebraNames = data.frontal
            .filter(([name]) =>
              data.lateral.some(([lateralName]) => lateralName === name)
            )
            .map(([name]) => name);
        } else if (data.frontal.length > 0) {
          // Only frontal has data
          vertebraNames = data.frontal.map(([name]) => name);
        } else if (data.lateral.length > 0) {
          // Only lateral has data
          vertebraNames = data.lateral.map(([name]) => name);
        }

        // Set the first vertebra as default, if any are available
        if (vertebraNames.length > 0) {
          setSelectedVertebra(vertebraNames[0]);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailableVertebrae();
  }, [renderClient]);

  // Generate vertebra items from whichever view(s) have data
  // Backend provides Sacrum -> L5 -> ... -> C1, we need to reverse to C1 -> ... -> L5 -> Sacrum
  const vertebraItems = (() => {
    if (
      availableVertebrae.frontal.length > 0 &&
      availableVertebrae.lateral.length > 0
    ) {
      // Both views have data - use common vertebrae, reverse the order
      const commonVertebrae = availableVertebrae.frontal.filter(([name]) =>
        availableVertebrae.lateral.some(([lateralName]) => lateralName === name)
      );
      return commonVertebrae.reverse().map(([vertebra]) => ({
        value: vertebra,
        label: vertebraUtils.getVertebraLabel(vertebra),
      }));
    } else if (availableVertebrae.frontal.length > 0) {
      // Only frontal has data
      return availableVertebrae.frontal
        .slice()
        .reverse()
        .map(([vertebra]) => ({
          value: vertebra,
          label: vertebraUtils.getVertebraLabel(vertebra),
        }));
    } else if (availableVertebrae.lateral.length > 0) {
      // Only lateral has data
      return availableVertebrae.lateral
        .slice()
        .reverse()
        .map(([vertebra]) => ({
          value: vertebra,
          label: vertebraUtils.getVertebraLabel(vertebra),
        }));
    }
    return [];
  })();

  const handleVertebraChange = event => {
    const value = event.target.value;
    setSelectedVertebra(value);
  };

  const handleAddVertebra = React.useCallback(async () => {
    if (!selectedVertebra) {
      return;
    }

    try {
      const frontalData = availableVertebrae.frontal.find(
        ([name]) => name === selectedVertebra
      )?.[1];
      const lateralData = availableVertebrae.lateral.find(
        ([name]) => name === selectedVertebra
      )?.[1];

      // Build vertebra data with only the views that have data
      const vertebraData = {
        vertebraName: selectedVertebra,
      };

      if (frontalData) {
        vertebraData.frontal = frontalData;
      }
      if (lateralData) {
        vertebraData.lateral = lateralData;
      }

      // Make sure we have at least one view's data
      if (!frontalData && !lateralData) {
        return;
      }

      await renderClient.addVertebra(PLANNING_STATES.VALIDATE, vertebraData);
      onClose?.();
    } catch (error) {}
  }, [selectedVertebra, availableVertebrae, renderClient, onClose]);

  if (isLoading) {
    return (
      <div className="vertebra-management-container">
        <div>Loading available vertebrae...</div>
      </div>
    );
  }

  if (!vertebraItems.length) {
    return (
      <div className="vertebra-management-container">
        <div>No vertebrae available</div>
      </div>
    );
  }

  const isSingleVertebra = vertebraItems.length === 1;

  return (
    <div className="vertebra-management-container">
      <LabeledSelect
        label="Select Vertebra"
        items={vertebraItems}
        selectedItem={selectedVertebra}
        onChange={handleVertebraChange}
        disable={isLoading || isSingleVertebra}
      />
      <div className="vertebra-management-actions">
        <Button
          className="App-button"
          value="Add Vertebra"
          onClick={handleAddVertebra}
          disabled={!selectedVertebra}
        />
      </div>
    </div>
  );
};

export default AddVertebra;
